import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { AdminLayout } from '../../components/admin/AdminLayout';
import { Search, Download, ChevronLeft, ChevronRight, X } from 'lucide-react';

const ORDER_STATUS_COLORS = {
  new: 'bg-blue-100 text-blue-700',
  processing: 'bg-yellow-100 text-yellow-700',
  shipped: 'bg-purple-100 text-purple-700',
  delivered: 'bg-green-100 text-green-700',
  cancelled: 'bg-red-100 text-red-600',
};

const PAYMENT_COLORS = {
  pending: 'bg-yellow-100 text-yellow-700',
  paid: 'bg-green-100 text-green-700',
  failed: 'bg-red-100 text-red-600',
  refunded: 'bg-gray-100 text-gray-600',
};

const ORDER_STATUSES = ['new', 'processing', 'shipped', 'delivered', 'cancelled'];
const PAYMENT_STATUSES = ['pending', 'paid', 'failed', 'refunded'];

export default function OrdersPage() {
  const { api } = useAuth();
  const [data, setData] = useState({ items: [], total: 0, page: 1, pages: 1 });
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ order_status: '', payment_status: '', search: '' });
  const [selected, setSelected] = useState(null);
  const [editFields, setEditFields] = useState({});
  const [saving, setSaving] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [noteSaving, setNoteSaving] = useState(false);
  const [noteMessage, setNoteMessage] = useState('');

  const fetchData = useCallback(async (page = 1) => {
    setLoading(true);
    const params = new URLSearchParams({ page, limit: 15 });
    if (filters.order_status) params.set('order_status', filters.order_status);
    if (filters.payment_status) params.set('payment_status', filters.payment_status);
    if (filters.search) params.set('search', filters.search);
    try {
      const { data: d } = await api.get(`/admin/orders?${params}`);
      setData(d);
    } finally {
      setLoading(false);
    }
  }, [api, filters]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openDetail = (item) => {
    setSelected(item);
    setEditFields({ order_status: item.order_status, payment_status: item.payment_status, tracking_number: item.tracking_number || '' });
    setNewNote('');
    setNoteMessage('');
  };

  const saveOrder = async () => {
    if (!selected) return;
    setSaving(true);
    await api.put(`/admin/orders/${selected.id}`, editFields);
    setSaving(false);
    setSelected(null);
    fetchData(data.page);
  };

  const addNote = async () => {
    if (!selected || !newNote.trim()) return;
    setNoteSaving(true);
    setNoteMessage('');
    try {
      const { data: res } = await api.post(`/admin/orders/${selected.id}/notes`, { text: newNote.trim() });
      setSelected((p) => ({ ...p, note_history: res.note_history }));
      setNewNote('');
      setNoteMessage('Note saved');
      setTimeout(() => setNoteMessage(''), 3000);
    } catch {
      setNoteMessage('Failed to save note');
    } finally {
      setNoteSaving(false);
    }
  };

  const exportCsv = async () => {
    const { data: blob } = await api.get('/admin/orders/export/csv', { responseType: 'blob' });
    const url = URL.createObjectURL(new Blob([blob]));
    const a = document.createElement('a');
    a.href = url;
    a.download = 'book_orders.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <AdminLayout>
      <div data-testid="admin-orders">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-medium" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>Book Orders</h1>
            <p className="text-sm text-gray-500">Manage book purchases and shipments</p>
          </div>
          <button onClick={exportCsv} className="inline-flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg text-sm hover:bg-gray-50 transition-colors" data-testid="export-orders-csv">
            <Download className="w-4 h-4" /> Export CSV
          </button>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3 mb-5">
          <div className="relative flex-1 min-w-[200px] max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search name or email..."
              value={filters.search}
              onChange={(e) => setFilters((p) => ({ ...p, search: e.target.value }))}
              className="w-full pl-9 pr-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#C4A484]"
              data-testid="order-search"
            />
          </div>
          <select value={filters.order_status} onChange={(e) => setFilters((p) => ({ ...p, order_status: e.target.value }))} className="px-3 py-2.5 border border-gray-200 rounded-lg text-sm bg-white" data-testid="order-status-filter">
            <option value="">All Order Statuses</option>
            {ORDER_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select value={filters.payment_status} onChange={(e) => setFilters((p) => ({ ...p, payment_status: e.target.value }))} className="px-3 py-2.5 border border-gray-200 rounded-lg text-sm bg-white" data-testid="payment-status-filter">
            <option value="">All Payments</option>
            {PAYMENT_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-8 h-8 border-2 border-[#C4A484] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : data.items.length === 0 ? (
            <div className="text-center py-16 text-gray-400 text-sm">No orders found</div>
          ) : (
            <table className="w-full text-sm" data-testid="orders-table">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  <th className="text-left px-5 py-3 text-xs uppercase tracking-wider text-gray-400 font-medium">Customer</th>
                  <th className="text-left px-5 py-3 text-xs uppercase tracking-wider text-gray-400 font-medium">Qty</th>
                  <th className="text-left px-5 py-3 text-xs uppercase tracking-wider text-gray-400 font-medium">Total</th>
                  <th className="text-left px-5 py-3 text-xs uppercase tracking-wider text-gray-400 font-medium">Payment</th>
                  <th className="text-left px-5 py-3 text-xs uppercase tracking-wider text-gray-400 font-medium">Status</th>
                  <th className="text-left px-5 py-3 text-xs uppercase tracking-wider text-gray-400 font-medium">Date</th>
                  <th className="text-left px-5 py-3 text-xs uppercase tracking-wider text-gray-400 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {data.items.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50/50 transition-colors" data-testid={`order-row-${item.id}`}>
                    <td className="px-5 py-3.5">
                      <p className="font-medium text-gray-900">{item.full_name}</p>
                      <p className="text-xs text-gray-400">{item.email}</p>
                    </td>
                    <td className="px-5 py-3.5">{item.quantity}</td>
                    <td className="px-5 py-3.5 font-medium">${item.total_amount?.toFixed(2)}</td>
                    <td className="px-5 py-3.5">
                      <span className={`text-xs px-2.5 py-1 rounded-full capitalize ${PAYMENT_COLORS[item.payment_status] || 'bg-gray-100'}`}>{item.payment_status}</span>
                    </td>
                    <td className="px-5 py-3.5">
                      <span className={`text-xs px-2.5 py-1 rounded-full capitalize ${ORDER_STATUS_COLORS[item.order_status] || 'bg-gray-100'}`}>{item.order_status}</span>
                    </td>
                    <td className="px-5 py-3.5 text-xs text-gray-400">{new Date(item.created_at).toLocaleDateString()}</td>
                    <td className="px-5 py-3.5">
                      <button onClick={() => openDetail(item)} className="text-xs text-[#C4A484] hover:underline" data-testid={`view-order-${item.id}`}>Manage</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {data.pages > 1 && (
            <div className="flex items-center justify-between px-5 py-3 border-t border-gray-100">
              <span className="text-xs text-gray-400">Page {data.page} of {data.pages} ({data.total} total)</span>
              <div className="flex gap-2">
                <button onClick={() => fetchData(data.page - 1)} disabled={data.page <= 1} className="p-1.5 border border-gray-200 rounded-lg disabled:opacity-30 hover:bg-gray-50"><ChevronLeft className="w-4 h-4" /></button>
                <button onClick={() => fetchData(data.page + 1)} disabled={data.page >= data.pages} className="p-1.5 border border-gray-200 rounded-lg disabled:opacity-30 hover:bg-gray-50"><ChevronRight className="w-4 h-4" /></button>
              </div>
            </div>
          )}
        </div>

        {/* Detail Drawer */}
        {selected && (
          <div className="fixed inset-0 z-50 flex justify-end" data-testid="order-detail-drawer">
            <div className="absolute inset-0 bg-black/30" onClick={() => setSelected(null)} />
            <div className="relative w-full max-w-md bg-white h-full shadow-xl overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between">
                <h2 className="text-lg font-medium">Order Details</h2>
                <button onClick={() => setSelected(null)} className="p-1 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5" /></button>
              </div>
              <div className="p-6 space-y-5">
                <div className="grid grid-cols-2 gap-4">
                  <div><p className="text-xs text-gray-400 mb-1">Customer</p><p className="text-sm font-medium">{selected.full_name}</p></div>
                  <div><p className="text-xs text-gray-400 mb-1">Email</p><p className="text-sm">{selected.email}</p></div>
                  <div><p className="text-xs text-gray-400 mb-1">Phone</p><p className="text-sm">{selected.phone || '—'}</p></div>
                  <div><p className="text-xs text-gray-400 mb-1">Quantity</p><p className="text-sm">{selected.quantity}</p></div>
                  <div><p className="text-xs text-gray-400 mb-1">Total</p><p className="text-sm font-semibold">${selected.total_amount?.toFixed(2)}</p></div>
                  <div><p className="text-xs text-gray-400 mb-1">Date</p><p className="text-sm">{new Date(selected.created_at).toLocaleString()}</p></div>
                </div>

                {selected.shipping_address && (
                  <div>
                    <p className="text-xs text-gray-400 mb-1">Shipping Address</p>
                    <p className="text-sm bg-gray-50 rounded-lg p-3">
                      {selected.shipping_address.street}<br />
                      {selected.shipping_address.city}, {selected.shipping_address.state} {selected.shipping_address.zip_code}<br />
                      {selected.shipping_address.country}
                    </p>
                  </div>
                )}

                <div>
                  <label className="block text-xs text-gray-400 mb-1">Payment Status</label>
                  <select value={editFields.payment_status} onChange={(e) => setEditFields((p) => ({ ...p, payment_status: e.target.value }))} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" data-testid="edit-payment-status">
                    {PAYMENT_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>

                <div>
                  <label className="block text-xs text-gray-400 mb-1">Order Status</label>
                  <select value={editFields.order_status} onChange={(e) => setEditFields((p) => ({ ...p, order_status: e.target.value }))} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" data-testid="edit-order-status">
                    {ORDER_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>

                <div>
                  <label className="block text-xs text-gray-400 mb-1">Tracking Number</label>
                  <input type="text" value={editFields.tracking_number} onChange={(e) => setEditFields((p) => ({ ...p, tracking_number: e.target.value }))} placeholder="Enter tracking number" className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#C4A484]" data-testid="edit-tracking" />
                </div>

                <button onClick={saveOrder} disabled={saving} className="w-full py-3 bg-[#1A1A1A] text-white text-sm font-medium rounded-lg hover:bg-[#C4A484] transition-colors disabled:opacity-50" data-testid="save-order-btn">
                  {saving ? 'Saving...' : 'Save Status & Tracking'}
                </button>

                <hr className="border-gray-100" />

                <div>
                  <p className="text-xs font-medium text-gray-600 mb-3">Notes</p>
                  <div className="flex gap-2 mb-2">
                    <textarea
                      value={newNote}
                      onChange={(e) => setNewNote(e.target.value)}
                      rows={2}
                      placeholder="Add a note..."
                      className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#C4A484] resize-none"
                      data-testid="order-note-input"
                    />
                  </div>
                  <div className="flex items-center gap-3 mb-4">
                    <button
                      onClick={addNote}
                      disabled={noteSaving || !newNote.trim()}
                      className="px-4 py-2 bg-[#1A1A1A] text-white text-xs font-medium rounded-lg hover:bg-[#C4A484] transition-colors disabled:opacity-40"
                      data-testid="add-order-note-btn"
                    >
                      {noteSaving ? 'Saving...' : 'Add Note'}
                    </button>
                    {noteMessage && (
                      <span className={`text-xs ${noteMessage.includes('Failed') ? 'text-red-500' : 'text-green-600'}`} data-testid="order-note-save-message">
                        {noteMessage}
                      </span>
                    )}
                  </div>
                  <div className="space-y-0" data-testid="order-note-history">
                    {(selected.note_history || []).length === 0 && !selected.notes && (
                      <p className="text-xs text-gray-400 italic">No notes yet</p>
                    )}
                    {selected.notes && !(selected.note_history || []).length && (
                      <div className="relative pl-4 pb-4 border-l-2 border-gray-200">
                        <div className="absolute -left-[5px] top-0 w-2 h-2 rounded-full bg-gray-300" />
                        <p className="text-sm text-gray-700">{selected.notes}</p>
                        <p className="text-[10px] text-gray-400 mt-1">Legacy note</p>
                      </div>
                    )}
                    {[...(selected.note_history || [])].reverse().map((note, i) => (
                      <div key={`note-${i}`} className="relative pl-4 pb-4 border-l-2 border-[#C4A484]/30 last:border-l-0 last:pb-0">
                        <div className="absolute -left-[5px] top-0 w-2 h-2 rounded-full bg-[#C4A484]" />
                        <p className="text-sm text-gray-700">{note.text}</p>
                        <p className="text-[10px] text-gray-400 mt-1">
                          {new Date(note.created_at).toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
